import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Save, Eye, Image as ImageIcon, Code, Clock, History, RotateCcw, X } from 'lucide-react';
import SummerNoteEditor from './SummerNoteEditor';
import BoardMembersManager from './BoardMembersManager';
import AdmissionEmailManager from './AdmissionEmailManager';

const SiteManagement = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [content, setContent] = useState('');
  const [headerImage, setHeaderImage] = useState(null);
  const [headerImagePreview, setHeaderImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' ou 'html'
  
  // Historique par page
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyPage, setHistoryPage] = useState(null);
  const [pageVersions, setPageVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/site/pages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error('Erreur chargement pages:', error);
    }
  };

  // Récupérer l'historique d'une page spécifique
  const fetchPageHistory = async (page) => {
    setHistoryLoading(true);
    setHistoryPage(page);
    setShowHistoryModal(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/site/pages/${page.slug}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPageVersions(data.versions || []);
      } else {
        setPageVersions([]);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setPageVersions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Restaurer une version spécifique d'une page
  const restorePageVersion = async (versionId) => {
    if (!historyPage) return;
    
    if (!window.confirm(`⚠️ Restaurer cette version de la page "${historyPage.title}" ?\n\nL'état actuel sera sauvegardé automatiquement avant la restauration.`)) {
      return;
    }
    
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/site/pages/${historyPage.slug}/restore/${versionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Page "${historyPage.title}" restaurée avec succès !` });
        fetchPages();
        // Re-sélectionner la page pour voir les changements
        if (selectedPage?.slug === historyPage.slug) {
          const updatedPages = await fetch(`${API_BASE}/api/admin/site/pages`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json());
          const updatedPage = updatedPages.pages?.find(p => p.slug === historyPage.slug);
          if (updatedPage) {
            selectPage(updatedPage);
          }
        }
        setShowHistoryModal(false);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la restauration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la restauration' });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Supprimer une version de l'historique
  const deletePageVersion = async (versionId) => {
    if (!historyPage) return;
    if (!window.confirm('Supprimer cette version de l\'historique ?')) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/site/pages/${historyPage.slug}/history/${versionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setPageVersions(prev => prev.filter(v => v.id !== versionId));
        setMessage({ type: 'success', text: 'Version supprimée' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectPage = (page) => {
    setSelectedPage(page);
    setContent(page.content || '');
    // Construire l'URL complète pour la preview
    if (page.header_image_url) {
      // Si c'est une URL relative, ajouter le domaine backend
      const imageUrl = page.header_image_url.startsWith('http') 
        ? page.header_image_url 
        : `${API_BASE}${page.header_image_url}`;
      setHeaderImagePreview(imageUrl);
    } else {
      setHeaderImagePreview(null);
    }
    setHeaderImage(null);
    setMessage(null);
  };

  const handleHeaderImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderImage(file);
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadHeaderImage = async () => {
    if (!headerImage || !selectedPage) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', headerImage);

      const response = await fetch(
        `${API_BASE}/api/admin/site/pages/${selectedPage.slug}/upload-header`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: 'Image header uploadée avec succès !' });
        // L'URL est maintenant complète depuis le backend
        setHeaderImagePreview(data.header_image_url);
        setHeaderImage(null);
        fetchPages();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de l\'upload de l\'image' });
      }
    } catch (error) {
      console.error('Erreur upload header:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'upload' });
    } finally {
      setLoading(false);
    }
  };

  const deleteHeaderImage = async () => {
    if (!selectedPage || !selectedPage.header_image) return;

    if (!confirm('Êtes-vous sûr de vouloir supprimer l\'image header ?')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE}/api/admin/site/pages/${selectedPage.slug}/header`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Image header supprimée !' });
        setHeaderImagePreview(null);
        setHeaderImage(null);
        fetchPages();
      }
    } catch (error) {
      console.error('Erreur suppression header:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    if (!selectedPage) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE}/api/admin/site/pages/${selectedPage.slug}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content }),
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Contenu sauvegardé avec succès !' });
        fetchPages();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde contenu:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 
          className="text-2xl font-bold mb-2"
          style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
        >
          Gestion des Pages du Site
        </h1>
        <p className="text-gray-600">
          Gérez les images header et le contenu des pages publiques du site. 
          <span className="text-sm text-gray-500 ml-1">
            Cliquez sur <History className="w-4 h-4 inline mx-1" /> pour voir l'historique d'une page.
          </span>
        </p>
      </div>

      {/* Modal Historique par Page */}
      {showHistoryModal && historyPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique : {historyPage.title}
              </h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Chargement...</p>
                </div>
              ) : pageVersions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun historique pour cette page</p>
                  <p className="text-sm mt-2">L'historique sera créé automatiquement à chaque modification.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pageVersions.map((version, index) => (
                    <div 
                      key={version.id} 
                      className={`p-4 rounded-lg border ${index === 0 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {formatDate(version.created_at)}
                            </span>
                            {index === 0 && (
                              <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">
                                Plus récent
                              </span>
                            )}
                            {version.has_header_image && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Avec image
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            par {version.created_by}
                          </div>
                          {version.content_preview && (
                            <div className="text-xs text-gray-400 mt-2 truncate">
                              {version.content_preview}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => restorePageVersion(version.id)}
                            disabled={historyLoading}
                            className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded text-sm hover:bg-amber-600 disabled:opacity-50"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Restaurer
                          </button>
                          <button
                            onClick={() => deletePageVersion(version.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <Clock className="w-4 h-4 inline mr-1" />
                Max. 20 versions conservées par page
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div 
          className={`mb-4 p-4 rounded ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des pages */}
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'rgb(196, 150, 96)' }}>
            Pages du site
          </h2>
          <div className="space-y-2">
            {pages.map((page) => (
              <div
                key={page.slug}
                className={`rounded border transition-colors ${
                  selectedPage?.slug === page.slug
                    ? 'border-2 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                style={{
                  borderColor: selectedPage?.slug === page.slug ? 'rgb(196, 150, 96)' : undefined
                }}
              >
                <div className="flex items-center">
                  <button
                    onClick={() => selectPage(page)}
                    className="flex-1 text-left px-4 py-3"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{page.title}</div>
                        <div className="text-xs text-gray-500">/{page.slug}</div>
                      </div>
                      {page.header_image && (
                        <ImageIcon className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                  </button>
                  {/* Bouton Historique */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchPageHistory(page);
                    }}
                    className="px-3 py-3 border-l border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-amber-600 transition-colors"
                    title={`Voir l'historique de ${page.title}`}
                  >
                    <History className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gestion de la page sélectionnée */}
        {selectedPage ? (
          <div className="lg:col-span-2 space-y-6">
            {/* Header Image - Masqué pour la page d'accueil */}
            {selectedPage.slug !== 'home-welcome' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'rgb(196, 150, 96)' }}>
                  Image Header - {selectedPage.title}
                </h2>

                {headerImagePreview && (
                  <div className="mb-4">
                    <img
                      src={headerImagePreview}
                      alt="Header preview"
                      className="w-full h-48 object-cover rounded border"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeaderImageChange}
                      className="hidden"
                    />
                    <div 
                      className="cursor-pointer px-4 py-2 rounded text-white text-center flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      <Upload className="w-4 h-4" />
                      {headerImage ? 'Changer l\'image' : 'Sélectionner une image'}
                    </div>
                  </label>

                  {headerImage && (
                    <button
                      onClick={uploadHeaderImage}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </button>
                  )}

                  {selectedPage.header_image && (
                    <button
                      onClick={deleteHeaderImage}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded flex items-center gap-2 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Contenu éditable */}
            {selectedPage.is_editable && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
                    Contenu de la page
                  </h2>
                  <div className="flex items-center gap-2">
                    {/* Toggle Visual/HTML */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('visual')}
                        className={`px-3 py-1 rounded flex items-center gap-1 transition-colors ${
                          viewMode === 'visual'
                            ? 'bg-white shadow text-gray-900'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Eye className="w-4 h-4" />
                        Visuel
                      </button>
                      <button
                        onClick={() => setViewMode('html')}
                        className={`px-3 py-1 rounded flex items-center gap-1 transition-colors ${
                          viewMode === 'html'
                            ? 'bg-white shadow text-gray-900'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Code className="w-4 h-4" />
                        HTML
                      </button>
                    </div>
                    
                    <button
                      onClick={saveContent}
                      disabled={loading}
                      className="px-4 py-2 rounded text-white flex items-center gap-2 hover:opacity-80"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      <Save className="w-4 h-4" />
                      Sauvegarder
                    </button>
                  </div>
                </div>

                {viewMode === 'visual' ? (
                  <SummerNoteEditor
                    value={content}
                    onChange={setContent}
                    height={400}
                  />
                ) : (
                  <div className="border rounded">
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="w-full h-96 p-4 font-mono text-sm"
                      style={{
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        resize: 'vertical'
                      }}
                      placeholder="Code HTML de la page..."
                    />
                    <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 border-t">
                      Mode édition HTML - Vous pouvez modifier directement le code source
                    </div>
                  </div>
                )}
              </div>
            )}

            {!selectedPage.is_editable && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  <strong>Note :</strong> Cette page a un contenu dynamique et ne peut pas être éditée ici.
                </p>
              </div>
            )}

            {/* Gestion des membres du conseil (uniquement pour la page "Conseil") */}
            {selectedPage.slug === 'conseil' && selectedPage.has_board_members && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-6" style={{ color: 'rgb(196, 150, 96)' }}>
                  Membres du Conseil d'Administration
                </h2>
                <BoardMembersManager />
              </div>
            )}

            {/* Gestion de l'email pour la page Admission */}
            {selectedPage.slug === 'admission' && selectedPage.has_admission_form && (
              <AdmissionEmailManager pageData={selectedPage} onUpdate={fetchPages} />
            )}
          </div>
        ) : (
          <div className="lg:col-span-2 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center p-12">
            <p className="text-gray-500 text-center">
              Sélectionnez une page dans la liste pour la gérer
            </p>
          </div>
        )}
      </div>

      {/* Section Vidéo Hero YouTube (toujours visible) */}
      <HeroVideoManager />
    </div>
  );
};

// Composant pour gérer la vidéo du hero
const HeroVideoManager = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [videoUrl, setVideoUrl] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [overlayOpacity, setOverlayOpacity] = useState(30);
  const [loading, setLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/settings/hero`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVideoUrl(data.video_url || '');
        setCoverImageUrl(data.cover_image_url || '');
        setOverlayOpacity(data.overlay_opacity !== undefined ? data.overlay_opacity : 30);
      }
    } catch (error) {
      console.error('Erreur chargement settings:', error);
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier vidéo' });
      return;
    }
    
    if (file.size > 100 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Le fichier est trop volumineux (max 100MB)' });
      return;
    }
    
    setUploadingVideo(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE}/api/admin/upload-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setVideoUrl(data.url || data.file_url);
        setMessage({ type: 'success', text: 'Vidéo uploadée !' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur upload' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur upload vidéo' });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner une image' });
      return;
    }
    
    setUploadingImage(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE}/api/admin/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoverImageUrl(data.url || data.file_url);
        setMessage({ type: 'success', text: 'Image uploadée !' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur upload' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur upload image' });
    } finally {
      setUploadingImage(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/settings/hero`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_url: videoUrl,
          cover_image_url: coverImageUrl,
          overlay_opacity: overlayOpacity
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Paramètres enregistrés !' });
        setIsEditing(false);
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
          🎬 Vidéo du Hero (Page d'accueil)
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 rounded text-white"
            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
          >
            Modifier
          </button>
        )}
      </div>

      <div className="space-y-4">
        {!isEditing ? (
          /* Mode affichage */
          <div className="space-y-3">
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm text-gray-600 mb-1">🎥 Vidéo MP4 :</p>
              <p className="font-mono text-sm break-all">{videoUrl || 'Aucune vidéo configurée'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm text-gray-600 mb-1">🖼️ Image de couverture :</p>
              {coverImageUrl ? (
                <div className="flex items-center gap-3">
                  <img src={`${API_BASE}${coverImageUrl}`} alt="Cover" className="w-20 h-12 object-cover rounded" />
                  <p className="font-mono text-xs break-all flex-1">{coverImageUrl}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucune image configurée</p>
              )}
            </div>
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm text-gray-600 mb-2">Intensité de l'overlay : {overlayOpacity}%</p>
              <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="bg-black h-full" style={{ width: `${overlayOpacity}%` }}></div>
              </div>
            </div>
          </div>
        ) : (
          /* Mode édition */
          <>
            {/* Vidéo MP4 */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🎥 Vidéo MP4 (PC & Mobile)
              </label>
              
              {videoUrl && (
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-800">✅ Vidéo configurée</p>
                    <p className="font-mono text-xs break-all">{videoUrl}</p>
                  </div>
                  <button onClick={() => setVideoUrl('')} className="text-red-600 hover:text-red-800 text-sm">
                    Supprimer
                  </button>
                </div>
              )}
              
              <input
                type="file"
                accept="video/mp4,video/webm"
                onChange={(e) => e.target.files[0] && handleVideoUpload(e.target.files[0])}
                disabled={uploadingVideo}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
              />
              {uploadingVideo && <p className="mt-2 text-sm text-amber-600">⏳ Upload en cours...</p>}
              <p className="mt-1 text-xs text-gray-500">Max 100MB. Formats : MP4, WebM. L'autoplay fonctionne sur tous les appareils.</p>
            </div>

            {/* Image de couverture */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🖼️ Image de couverture (affichée pendant le chargement)
              </label>
              
              {coverImageUrl && (
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={`${API_BASE}${coverImageUrl}`} alt="Cover" className="w-24 h-16 object-cover rounded" />
                    <p className="font-mono text-xs break-all">{coverImageUrl}</p>
                  </div>
                  <button onClick={() => setCoverImageUrl('')} className="text-red-600 hover:text-red-800 text-sm">
                    Supprimer
                  </button>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                disabled={uploadingImage}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {uploadingImage && <p className="mt-2 text-sm text-blue-600">⏳ Upload en cours...</p>}
              <p className="mt-1 text-xs text-gray-500">Cette image s'affiche pendant le chargement de la vidéo.</p>
            </div>

            {/* Overlay */}
            <div className="border rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intensité de l'overlay ({overlayOpacity}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={overlayOpacity}
                onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Boutons */}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={saveSettings}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2 hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => { setIsEditing(false); fetchSettings(); }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </>
        )}

        {message && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteManagement;
